/**
 * CompactEditSections — the editable body of the Compact VQE popover for a flat
 * SELECT / simple DML shape. Composes the SAME full-VQE clause primitives
 * (`SelectList`, `FromSection`, `FilterPills`, `OrderBySection`,
 * `LimitOffsetPanel`) in a denser single-panel layout. No parallel edit logic:
 * every add / remove / edit runs through the section components' existing
 * SR-Edit wiring, committing to the Monaco buffer via the shared `editContext`.
 *
 * Sections, in order (canonical source-first order, §3.4 S10): From + Joins
 * (FK-driven) · Columns (show/hide chooser) · Filters (WHERE) · Group By ·
 * Having · Sort / Limit (ORDER BY + read-only TOP/LIMIT/FETCH). The FROM card
 * carries both the table list and the FK-driven "add join" affordance, so
 * Joins live within it rather than as a separate card.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3
 * (compact surface) + §3.4 S10 (canonical source-first section order).
 */

import { useMemo, type ReactNode } from 'react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import type { CteOutline } from '../../render/select/extractCtes';
import type { SelectOutline } from '../../render/select/extractSelectOutline';
import type { SubqueryWiring } from '../../render/select/SelectRenderer';
import { buildCteNameEntries } from '../../render/select/fromCteRef';
import { ClauseAddRow } from '../../render/select/ClauseAddRow';
import { CompactCteStrip } from './CompactCteStrip';
import { FilterPills } from '../../render/select/FilterPills';
import { FromSection } from '../../render/select/FromSection';
import { GroupBySection } from '../../render/select/GroupBySection';
import { LimitOffsetPanel } from '../../render/select/LimitOffsetPanel';
import { OrderBySection } from '../../render/select/OrderBySection';
import { SelectList } from '../../render/select/SelectList';

export interface CompactEditSectionsProps {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly editContext: VqeEditContext | null;
  /**
   * Drill wiring for WHERE/HAVING-clause subqueries (Task 8; HAVING mount added
   * Plan 8 T1). Threaded ONLY into `FilterPills` (both the WHERE and HAVING
   * mounts) so a `col IN (SELECT …)` leaf renders a focusable chip whose focus
   * button drills into a nested compact editor. FROM/JOIN subqueries stay inert
   * in the compact surface (out of scope), so this is NOT passed there.
   */
  readonly subqueryCtx?: SubqueryWiring;
  /**
   * WITH-clause outline for this statement (spec 2026-07-28). Drives the
   * leading `CompactCteStrip` AND the FROM rows' CTE-ref badge — the same two
   * consumers `SelectRenderer` feeds from its own `extractCtes` memo. Absent /
   * `hasWith === false` renders neither.
   */
  readonly cteOutline?: CteOutline | null;
  /**
   * "Add CTE" for the main query while it has no WITH clause — sits on the
   * FROM card's action line, as in the full tab (on the empty "FROM — none"
   * row when there is no FROM). Only the base popover passes it; drilled
   * frames never do.
   */
  readonly cteAddAction?: ReactNode;
}

export function CompactEditSections({
  bufferText,
  outline,
  options,
  editContext,
  subqueryCtx,
  cteOutline,
  cteAddAction,
}: CompactEditSectionsProps) {
  const ctx = editContext ?? undefined;
  // CTE-name lookup for the FROM-item CTE-ref badge's name-match fallback (the
  // path taken whenever no edit-ready QueryStructure is reachable). Built off
  // the already-extracted bindings — no second parse of the WITH clause.
  const cteNames = useMemo(
    () => buildCteNameEntries(cteOutline?.bindings ?? [], bufferText, options),
    [cteOutline, bufferText, options],
  );
  // LimitOffsetPanel's row values are click-to-edit only once the schema
  // snapshot has landed — `useInlineToolbarEditContext` returns a non-null
  // context as soon as connection + statement resolve, BEFORE the async
  // snapshot refresh. Mirrors SelectRenderer's `limitEditContext` gate; the
  // other sections gate internally via `editReady`, LimitOffsetPanel doesn't.
  const limitCtx = ctx && ctx.service.getSchemaSnapshot() ? ctx : undefined;
  return (
    <div className="flex flex-col gap-2" data-testid="vqe-compact-sections">
      {/* With — one chip per CTE, drilling into that body via the LOCAL compact
          drill (`subqueryCtx.onDrill`). Leads the stack because the WITH clause
          precedes the main query in source order (§3.4 S10), and because the
          FROM rows below reference its names. */}
      {cteOutline?.hasWith ? (
        <CompactCteStrip outline={cteOutline} editContext={ctx} subqueryCtx={subqueryCtx} />
      ) : null}

      {/* From + Joins — table list with FK-driven [+ add join] / remove.
          `compact` (SEL-22) selects the dense single-line JOIN row layout so
          SELECT/WHERE below stay visible without scrolling — a pure display
          density change, no subqueryCtx (FROM/JOIN subqueries stay inert in
          the compact surface, per the doc comment above). */}
      <FromSection
        bufferText={bufferText}
        outline={outline}
        options={options}
        editContext={ctx}
        cteNames={cteNames}
        compact
        extraActions={cteAddAction}
      />

      {/* Columns — show/hide chooser + per-column remove (inline [+ Add]).
          `lockWindowColumns` (ANA-13/17/19): the compact surface has no
          structured OVER editor, so a window-function column renders READ-ONLY
          (locked chip + WindowSpec read-only summary) while every other column /
          clause stays editable — a per-column degrade, not a whole-query one.
          This mount is the compact surface for BOTH the base popover
          (`CompactVqeContent`) and a drilled subquery frame
          (`CompactNestedSections`), so both lock through this one prop. */}
      <SelectList bufferText={bufferText} outline={outline} options={options} editContext={ctx} compact lockWindowColumns />

      {/* Filters (WHERE) — add / edit operator + value / remove on pills.
          subqueryCtx threads here AND into the HAVING mount below (Task 8 /
          Plan 8 T1) — WHERE and HAVING share the same drill wiring. */}
      <FilterPills
        bufferText={bufferText}
        span={outline.where}
        options={options}
        kind="where"
        subqueryCtx={subqueryCtx}
        editContext={ctx}
        enableViewModes
        density="compact"
        hideEmpty
      />

      {/* Group / Having / Sort — in the full editor's mount order
          (`SelectRenderer.tsx`: GroupBySection → FilterPills kind="having" →
          OrderBySection). GROUP BY / HAVING edit inline exactly like the full
          view (Plan 8 T1). Empty clauses render nothing here (`hideEmpty`):
          the clause add row below offers them, as in the full view. */}
      <GroupBySection bufferText={bufferText} outline={outline} options={options} editContext={ctx} hideEmpty />
      <FilterPills
        bufferText={bufferText}
        span={outline.having}
        options={options}
        kind="having"
        subqueryCtx={subqueryCtx}
        editContext={ctx}
        enableViewModes
        density="compact"
        hideEmpty
      />
      <OrderBySection bufferText={bufferText} outline={outline} options={options} editContext={ctx} hideEmpty />

      {/* Limit — TOP / LIMIT / OFFSET / FETCH with inline numeric editing. */}
      <LimitOffsetPanel bufferText={bufferText} outline={outline} options={options} editContext={limitCtx} hideEmpty />

      {/* The empty WHERE / GROUP BY / HAVING / ORDER BY / LIMIT, as one row. */}
      <ClauseAddRow bufferText={bufferText} outline={outline} options={options} editContext={ctx} />
    </div>
  );
}
